import z from 'zod/v4';
import { createPresetSchema } from './utils';
import { DefaultPresetSchema } from './default-preset-schema';
import type { PresetConfig } from './types';
import type { SubTenant } from '@pigello/pigello-matrix';
import { DistributionInstructionsSchema } from './constants';
import { TENANT_TYPE } from '@/lib/tenant/constants';
import {
  CONTRACT_STAKEHOLDER_CHOICES,
  SUB_TENANT_CATEGORY_CHOICES,
  SUB_TENANT_CONNECTION_TYPES,
  TENANT_STAKEHOLDER_CHOICES,
} from '@/lib/sub-tenant/constants';

const V1DataSchema = z.object({
  title: z.string(),
  description: z.string(),

  restrictedToTenantType: z.enum(TENANT_TYPE).nullable(),

  ssnIsCoordinationNumber: z.boolean(),

  // Tenant portal settings
  allowObjectsAccess: z.boolean(),
  allowInvoiceAccess: z.boolean(),
  allowNotifications: z.boolean(),
  allowRevenueRowAccess: z.boolean(),
  allowPersonalInformationAccess: z.boolean(),
  allowBrfAccess: z.boolean(),
  allowErrandsAccess: z.boolean(),
  allowErrandsAccessThroughConnections: z.boolean(),
  allowContractsAccess: z.boolean(),
  allowCancelContractAccess: z.boolean(),

  connection: z
    .discriminatedUnion('connectionType', [
      z.object({
        connectionType: z.literal(SUB_TENANT_CONNECTION_TYPES.TENANT),
        stakeholderType: z.enum(TENANT_STAKEHOLDER_CHOICES),
        category: z.enum(SUB_TENANT_CATEGORY_CHOICES).nullable(),
        invoiceDistributionInstruction: DistributionInstructionsSchema,
        reminderDistributionInstruction: DistributionInstructionsSchema,
        debtDistributionInstruction: DistributionInstructionsSchema,
      }),
      z.object({
        connectionType: z.union([
          z.literal(SUB_TENANT_CONNECTION_TYPES.CONTRACT),
          z.literal(SUB_TENANT_CONNECTION_TYPES.BRF),
        ]),
        stakeholderType: z.enum(CONTRACT_STAKEHOLDER_CHOICES),
        category: z.enum(SUB_TENANT_CATEGORY_CHOICES).nullable(),
        invoiceDistributionInstruction: DistributionInstructionsSchema,
        reminderDistributionInstruction: DistributionInstructionsSchema,
        debtDistributionInstruction: DistributionInstructionsSchema,
      }),
    ])
    .nullable(),
} satisfies Partial<Record<keyof SubTenant | (string & {}), z.core.$ZodType>>);

export const V1Schema = createPresetSchema('1.0.0', V1DataSchema);

export const SUB_TENANT_PRESET_IDENTIFIER = 'subtenant.instance';

const SubTenantPresetSchema = z.object({
  ...DefaultPresetSchema.shape,
  context: z.literal(SUB_TENANT_PRESET_IDENTIFIER),
  data: V1Schema,
});

export const SubTenantPreset = {
  identifier: SUB_TENANT_PRESET_IDENTIFIER,
  currentSchema: SubTenantPresetSchema,
  currentVersion: '1.0.0',
  migrations: null,
  single: false,
} as const satisfies PresetConfig;

export type SubTenantPreset = z.infer<typeof SubTenantPresetSchema>;
export type SubTenantPresetData = SubTenantPreset['data']['presetData'];
